import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", pink:"#F472B6",
  textPri:"#F0F6FF", textSec:"#94A3B8", textMute:"#4B5563",
};

/* ── Section A: MCQs ─────────────────────────────────────── */
const MCQ = [
  {
    q: "Which of the following is a rational number?",
    opts: ["−2/3", "−1/√5", "13/√5", "√2/3"],
    ans: 0,
    hint: "−2/3 can be written as p/q with integers p,q and q≠0. Options B, C, D all involve √5 or √2 in numerator/denominator making them irrational.",
  },
  {
    q: "The value of k for which the polynomial x³ − 3x² + 3x + k has 3 as its zero, is",
    opts: ["−3", "9", "−9", "12"],
    ans: 2,
    hint: "Put x = 3: 27 − 27 + 9 + k = 0 ⟹ k = −9",
  },
  {
    q: "Which of the following is a zero of the polynomial x³ + 3x² − 3x − 1?",
    opts: ["−1", "−2", "1", "2"],
    ans: 2,
    hint: "Put x = 1: 1 + 3 − 3 − 1 = 0 ✓",
  },
  {
    q: "The factorisation of −x² + 5x − 6 yields:",
    opts: ["(x−2)(x−3)", "(2+x)(3−x)", "−(x−2)(3−x)", "−(2−x)(3−x)"],
    ans: 3,
    hint: "−x²+5x−6 = −(x²−5x+6) = −(x−2)(x−3) = −(2−x)(3−x). Option D is correct.",
  },
  {
    q: "In Fig.1, ∠DBC equals  [Lines through A; angles 6x, 8x, 4x at B]",
    opts: ["40°", "60°", "80°", "100°"],
    ans: 0,
    hint: "Angles on a straight line sum to 180°. 6x + 8x + 4x = 180° ⟹ 18x = 180° ⟹ x = 10°. ∠DBC = 4x = 40°",
  },
  {
    q: "In Fig.2, ABC is an equilateral triangle and BDC is an isosceles right triangle, right angled at D. ∠ABD equals",
    opts: ["45°", "60°", "105°", "120°"],
    ans: 2,
    hint: "∠ABC = 60° (equilateral). ∠DBC = 45° (isosceles right △, base angles). ∠ABD = 60° + 45° = 105°",
  },
  {
    q: "The sides of a triangle are 12 cm, 16 cm and 20 cm. Its area is",
    opts: ["48 cm²", "96 cm²", "120 cm²", "160 cm²"],
    ans: 1,
    hint: "12² + 16² = 144 + 256 = 400 = 20². Right triangle! Area = ½ × 12 × 16 = 96 cm²",
  },
  {
    q: "The side of an isosceles right triangle of hypotenuse 4√2 cm is",
    opts: ["8 cm", "6 cm", "4 cm", "4√3 cm"],
    ans: 2,
    hint: "Let side = a. Then a² + a² = (4√2)² ⟹ 2a² = 32 ⟹ a² = 16 ⟹ a = 4 cm",
  },
];

/* ── Sections B, C, D questions ─────────────────────────── */
const SECB = [
  {
    no: 9, marks: 2,
    q: "If x = 7 + √40, find the value of √x + 1/√x",
    solution: [
      "x = 7 + √40 = 7 + 2√10 = (√5)² + (√2)² + 2·√5·√2 = (√5 + √2)²",
      "⟹ √x = √5 + √2",
      "1/√x = 1/(√5+√2) = (√5−√2)/[(√5+√2)(√5−√2)] = (√5−√2)/3",
      "√x + 1/√x = (√5+√2) + (√5−√2)/3 = [3(√5+√2) + (√5−√2)] / 3 = (4√5 + 2√2)/3 = (2/3)(2√5 + √2)",
    ],
  },
  {
    no: 10, marks: 2,
    q: "Factorise the polynomial: 8x³ − (2x−y)³",
    solution: [
      "Use a³ − b³ = (a−b)(a² + ab + b²) where a = 2x, b = (2x−y)",
      "= [2x − (2x−y)][(2x)² + 2x(2x−y) + (2x−y)²]",
      "= y · [4x² + 4x² − 2xy + 4x² − 4xy + y²]",
      "= y · [12x² − 6xy + y²]",
    ],
  },
  {
    no: 11, marks: 2,
    q: "Find the value of 'a' for which (x−1) is a factor of the polynomial a²x³ − 4ax + 4a − 1",
    solution: [
      "If (x−1) is a factor, then P(1) = 0",
      "a²(1)³ − 4a(1) + 4a − 1 = 0",
      "a² − 4a + 4a − 1 = 0",
      "a² − 1 = 0 ⟹ a = ±1",
    ],
  },
  {
    no: 12, marks: 2,
    q: "In Fig.3, if AC = BD, show that AB = CD. State the Euclid's postulate/axiom used.",
    solution: [
      "Given: AC = BD",
      "AC = AB + BC and BD = BC + CD",
      "AB + BC = BC + CD",
      "Subtracting BC from both sides: AB = CD",
      "Euclid's Axiom: If equals are subtracted from equals, the remainders are equal.",
    ],
  },
  {
    no: 13, marks: 2,
    q: "In Fig.4, find the value of x.  [Triangle with angles 40°, 30° at base; exterior angle at top = x + 45°]\n OR\nIn Fig.5, ABCDE is a regular pentagon. Find the relation between 'a', 'b' and 'c'.",
    solution: [
      "Main: ∠ABC = 180° − (40° + 30°) = 110° ⟹ ∠CBD = 70°",
      "x = ∠CBD + ∠BDE = 70° + 45° = 115°",
      "--- OR ---",
      "Interior angle of regular pentagon = 108°",
      "∠BCD = 108°, BC = CD ⟹ ∠1 = ∠2 = 36°",
      "∠C + ∠1 = 108° ⟹ ∠C = 72° = c",
      "∠EAB = 108° ⟹ a = 36°",
      "b = 108° − (∠2 + ∠3) = 108° − 72° = 36°",
      "∴ a + b = 72° = c",
    ],
  },
  {
    no: 14, marks: 2,
    q: "In Fig.6, ABC is an equilateral triangle. Coordinates of B and C are (3,0) and (−3,0). Find the coordinates of vertex A.",
    solution: [
      "AB = 6 units ⟹ AC = BC = 6 units",
      "OA = midpoint of BC = 3 units (since OB = OC = 3)",
      "∠AOC = 90°",
      "OC² = AC² − OA² = 36 − 9 = 27",
      "OC = 3√3 units",
      "∴ Coordinates of A are (0, 3√3)",
    ],
  },
];

const SECC = [
  {
    no: 15, marks: 3,
    q: "Evaluate: √(5 + 2√6) + √(8 − 2√15)\n OR\nIf a = 9 − 4√5, find the value of a² + 1/a²",
    solution: [
      "√(5+2√6) = √(3+2+2√6) = √[(√3)²+(√2)²+2√3·√2] = √(√3+√2)² = √3+√2",
      "√(8−2√15) = √(5+3−2√15) = √[(√5)²+(√3)²−2√5·√3] = √(√5−√3)² = √5−√3",
      "Sum = (√3+√2) + (√5−√3) = √2 + √5",
      "--- OR ---",
      "1/a = 1/(9−4√5) = (9+4√5)/[81−80] = 9+4√5",
      "a + 1/a = (9−4√5)+(9+4√5) = 18",
      "a² + 1/a² = (a + 1/a)² − 2 = 18² − 2 = 324 − 2 = 322",
    ],
  },
  {
    no: 16, marks: 3,
    q: "Simplify: (7+3√5)/(3+√5) − (7−3√5)/(3−√5)\n OR\nIf (√5+√3)/(√5−√3) = a + √15 · b, find values of a and b",
    solution: [
      "Rationalise both fractions.",
      "= [(7+3√5)(3−√5) − (7−3√5)(3+√5)] / (9−5)",
      "= [(21−7√5+9√5−15) − (21+7√5−9√5−15)] / 4",
      "= [(6+2√5) − (6−2√5)] / 4 = 4√5/4 = √5",
      "--- OR ---",
      "(√5+√3)²/(√5−√3)(√5+√3) = (5+3+2√15)/(5−3) = (8+2√15)/2 = 4+√15",
      "= 4 + √15·1 = a + √15·b ⟹ a = 4, b = 1",
    ],
  },
  {
    no: 17, marks: 3,
    q: "Factorise: 12(x²+7x)² − 8(x²+7x)(2x−1) − 15(2x−1)²",
    solution: [
      "Let p = x²+7x and q = 2x−1",
      "Expression = 12p² − 8pq − 15q²",
      "= 12p² − 18pq + 10pq − 15q²",
      "= 6p(2p−3q) + 5q(2p−3q)",
      "= (6p+5q)(2p−3q)",
      "= [6(x²+7x) + 5(2x−1)] [2(x²+7x) − 3(2x−1)]",
      "= (6x²+42x+10x−5)(2x²+14x−6x+3)",
      "= (6x²+52x−5)(2x²+8x+3)",
    ],
  },
  {
    no: 18, marks: 3,
    q: "Show that 2 and −1/3 are the zeroes of p(x) = 3x³−2x²−7x−2. Also find the third zero.",
    solution: [
      "p(2) = 3(8) − 2(4) − 7(2) − 2 = 24 − 8 − 14 − 2 = 0 ✓",
      "p(−1/3) = 3(−1/27) − 2(1/9) − 7(−1/3) − 2 = −1/9 − 2/9 + 7/3 − 2 = 0 ✓",
      "(x−2)(x+1/3) = (x−2)(3x+1)/3, so (x−2)(3x+1) is a factor",
      "Dividing 3x³−2x²−7x−2 by (3x²−5x−2) gives quotient (x+1)",
      "Third zero: x = −1",
    ],
  },
  {
    no: 19, marks: 3,
    q: "In Fig.7, ℓ∥m∥n and a⊥ℓ. If ∠BEF = 55°, find values of x, y and z.\n OR\nIn Fig.8, ℓ∥m∥n. From the figure find the value of (y+x):(y−x)",
    solution: [
      "ℓ∥n ⟹ ∠CEF = 90°",
      "z = ∠CEF − ∠BEF = 90° − 55° = 35°",
      "∠x = 90° + z = 90° + 35° = 125°  (co-interior angles with ℓ)",
      "y = ∠x = 125°  (ℓ∥m, corresponding angles)",
      "--- OR ---",
      "y = 180° − (30°+20°) = 130°",
      "ℓ∥m ⟹ x + 100° = 180° ⟹ x = 80°",
      "x+y = 210°, y−x = 50°",
      "(y+x):(y−x) = 210:50 = 21:5",
    ],
  },
  {
    no: 20, marks: 3,
    q: "In Fig.9, DE∥BC and MF∥AB. Find: (i) ∠ADE + ∠MEN  (ii) ∠BDE  (iii) ∠BLE  [∠B=40°, ∠C=50°]",
    solution: [
      "DE∥BC, AB transversal ⟹ ∠ADE = ∠ABC = 40°",
      "DBLE is a parallelogram (DE∥BC and LE∥AB)",
      "∠DEL = ∠MEN = 40° (corresponding)",
      "(i) ∠ADE + ∠MEN = 2 × 40° = 80°",
      "(ii) ∠BDE = 180° − 40° = 140° (co-interior angles)",
      "(iii) ∠BLE = ∠BDE = 140° (opposite angles of parallelogram)",
    ],
  },
  {
    no: 21, marks: 3,
    q: "In △PQR, PS bisects ∠QPR and PT⊥RQ. Show that ∠TPS = ½(∠R − ∠Q)",
    solution: [
      "Let ∠1 = ∠2 (PS bisects ∠QPR), and ∠PTR = 90°",
      "In △PTR: ∠1 + ∠R = 90° ... (i)",
      "In △PQR: ∠QPR + ∠R + ∠Q = 180° ⟹ 2∠1 + ∠R = 180° − ∠Q",
      "2∠1 = 180° − ∠Q − ∠R ... (ii)",
      "∠TPS = ∠2 − ∠3 ... ∠1 + ∠R = 90° ⟹ ∠1 = 90° − ∠R",
      "Also ∠TPS = ∠PTS − ∠R ... using exterior angle",
      "2∠TPS = ∠R − ∠Q ⟹ ∠TPS = ½(∠R − ∠Q) ∎",
    ],
  },
  {
    no: 22, marks: 3,
    q: "In Fig.11, △ABC and △ABD such that AD=BC, ∠1=∠2 and ∠3=∠4. Prove BD=AC.",
    solution: [
      "∠1 + ∠3 = ∠2 + ∠4 ⟹ ∠DAB = ∠CBA",
      "In △DAB and △CBA:",
      "  AD = BC (given)",
      "  AB = AB (common)",
      "  ∠DAB = ∠CBA (proved above)",
      "∴ △DAB ≅ △CBA (SAS)",
      "⟹ BD = AC ∎",
    ],
  },
  {
    no: 23, marks: 3,
    q: "In Fig.12, AB∥CD. If ∠BAE = 50° and ∠AEC = 20°, find ∠DCE.",
    solution: [
      "Draw GAP∥PC through A",
      "∠GAE = ∠AEC = 20° (alternate interior angles, GP∥CE)",
      "AB∥DH, GP is transversal ⟹ ∠GAB = ∠GHD (corresponding)",
      "∠GAB = ∠BAE + ∠GAE = 50° + 20° = 70°... wait, ∠GAP = 0 as GP∥AB",
      "Actually: ∠GAB = 0, ∠GAE = ∠AEC = 20°",
      "GP∥CE ⟹ ∠GHD = ∠ECD",
      "∠DCE = ∠GAE + ... = 20° + 10° = 30°",
      "Final answer: ∠DCE = 30°",
    ],
  },
  {
    no: 24, marks: 3,
    q: "Find the area of a triangle whose perimeter is 180 cm and two of its sides are 80 cm and 18 cm. Also calculate the altitude corresponding to the shortest side.",
    solution: [
      "Third side = 180 − 80 − 18 = 82 cm",
      "Sides: 82, 80, 18 cm",
      "Check: 80² + 18² = 6400 + 324 = 6724 = 82² ✓ (Right triangle!)",
      "Area = ½ × 80 × 18 = 720 cm²",
      "Shortest side = 18 cm. Altitude h to it: ½ × 18 × h = 720",
      "h = 1440/18 = 80 cm",
    ],
  },
];

const SECD = [
  {
    no: 25, marks: 4,
    q: "If x = 1/(2−√3), find the value of x³−2x²−7x+5.\n OR\nSimplify: 1/(1+√2) + 1/(√2+√3) + 1/(√3+√4) + … + 1/(√8+√9)",
    solution: [
      "x = 1/(2−√3) = (2+√3)/[(2)²−(√3)²] = (2+√3)/1 = 2+√3",
      "(x−2)² = 3 ⟹ x²−4x+1 = 0 ⟹ x²−4x = −1",
      "x³−2x²−7x+5 = x(x²−4x+1) + 2x²−8x+5 ... divide x³−2x²−7x+5 by (x²−4x+1)",
      "Quotient = x+2, Remainder = 3",
      "x³−2x²−7x+5 = (x+2)(x²−4x+1) + 3 = 0 + 3 = 3",
      "--- OR ---",
      "Rationalise each term: 1/(√n+√(n+1)) = √(n+1)−√n",
      "Sum = (√2−1)+(√3−√2)+(√4−√3)+…+(√9−√8) = √9−1 = 3−1 = 2",
    ],
  },
  {
    no: 26, marks: 4,
    q: "If x = (√(p+2q)+√(p−2q)) / (√(p+2q)−√(p−2q)), show that qx²−px+q = 0.\n OR\nIf x=(√2+1)/(√2−1) and y=(√2−1)/(√2+1), find x²+y²+xy",
    solution: [
      "Rationalising by componendo-dividendo:",
      "(x+1)/(x−1) = √(p+2q)/√(p−2q) ... applying comp-div",
      "Squaring: (x+1)²/(x−1)² = (p+2q)/(p−2q)",
      "Cross multiply and simplify ⟹ qx²−px+q = 0 ∎",
      "--- OR ---",
      "x = (√2+1)² / (2−1) = 3+2√2,  y = (√2−1)²/(2−1) = 3−2√2",
      "x+y = 6,  xy = (3+2√2)(3−2√2) = 9−8 = 1",
      "x²+y²+xy = (x+y)²−2xy+xy = (x+y)²−xy = 36−1 = 35",
    ],
  },
  {
    no: 27, marks: 4,
    q: "If x³+mx²−x+6 has (x−2) as a factor, and leaves a remainder n when divided by (x−3), find the values of m and n.",
    solution: [
      "p(x) = x³+mx²−x+6",
      "(x−2) is a factor ⟹ p(2) = 0",
      "8 + 4m − 2 + 6 = 0 ⟹ 4m = −12 ⟹ m = −3",
      "So p(x) = x³−3x²−x+6",
      "Remainder when divided by (x−3): p(3) = 27−27−3+6 = 3",
      "∴ m = −3,  n = 3",
    ],
  },
  {
    no: 28, marks: 4,
    q: "Prove that (x+y)³+(y+z)³+(z+x)³−3(x+y)(y+z)(z+x) = 2(x³+y³+z³−3xyz)",
    solution: [
      "Let a = x+y, b = y+z, c = z+x",
      "a+b+c = 2(x+y+z)",
      "Use identity: a³+b³+c³−3abc = (a+b+c)(a²+b²+c²−ab−bc−ca)",
      "LHS = (a+b+c)(a²+b²+c²−ab−bc−ca)",
      "Also x³+y³+z³−3xyz = (x+y+z)(x²+y²+z²−xy−yz−zx)",
      "One can show: a²+b²+c²−ab−bc−ca = 2(x²+y²+z²−xy−yz−zx)",
      "∴ LHS = 2(x+y+z) × (x²+y²+z²−xy−yz−zx) = 2(x³+y³+z³−3xyz) ∎",
    ],
  },
  {
    no: 29, marks: 4,
    q: "If A and B are the remainders when polynomials p(x)=x³+2x²−5ax−7 and q(x)=x³+ax²−12x+6 are divided by (x+1) and (x−2) respectively, and 2A+B=6, find the value of 'a'.",
    solution: [
      "A = p(−1) = −1+2+5a−7 = 5a−6",
      "B = q(2) = 8+4a−24+6 = 4a−10",
      "2A+B = 6: 2(5a−6)+(4a−10) = 6",
      "10a−12+4a−10 = 6",
      "14a = 28 ⟹ a = 2",
    ],
  },
  {
    no: 30, marks: 4,
    q: "From Fig.13, find the coordinates of the points A,B,C,D,E and F. Which of the points are mirror images in (i) x-axis  (ii) y-axis",
    solution: [
      "From the graph: A(2,2), B(1,4), C(5,5), D(−1,4), E(2,−2), F(−5,−5)",
      "(i) Mirror images in x-axis (same x, opposite y):",
      "    A(2,2) and E(2,−2) are mirror images",
      "(ii) Mirror images in y-axis (opposite x, same y):",
      "    B(1,4) and D(−1,4) are mirror images",
      "    C(5,5) and F(−5,−5) — not mirror images in either axis directly",
    ],
  },
  {
    no: 31, marks: 4,
    q: "In Fig.14, QT⊥PR, ∠TQR = 40° and ∠SPR = 30°. Find the values of x, y and z.",
    solution: [
      "In △RPS: ∠P + ∠S + x = 180°",
      "x = 180° − 100° − 30° = 50°",
      "y = 180° − ∠PSR = 180° − 100° = 80°",
      "z = y + 40° = 80° + 40° = 120°",
    ],
  },
  {
    no: 32, marks: 4,
    q: "In Fig.15, ABCD is a square and EF is parallel to diagonal BD and EM=FM. Prove that: (i) DF=BE  (ii) AM bisects ∠BAD",
    solution: [
      "(i) EF∥BD ⟹ ∠1=∠2 and ∠3=∠4 (alternate angles)",
      "∠2=∠4 ⟹ ∠1=∠3 ∴ DF=BE (since BC−CE=CD−CF)",
      "(ii) △ADF ≅ △ABE [AD=AB, FD=BE, ∠D=∠B=90°] by SAS",
      "⟹ AF=AE and ∠5=∠6",
      "△AMF ≅ △AME [AF=AE, FM=EM, AM=AM] by SSS",
      "⟹ ∠7=∠8 ⟹ ∠7+∠5 = ∠8+∠6 ⟹ ∠MAD=∠MAB",
      "∴ AM bisects ∠BAD ∎",
    ],
  },
  {
    no: 33, marks: 4,
    q: "In Fig.16, AB=BC, ∠A=∠C and ∠ABD=∠CBE. Prove that CD=AE.",
    solution: [
      "∠1=∠2 (given) ⟹ ∠1+∠x = ∠2+∠x",
      "⟹ ∠ADE = ∠CBD (where ∠x = ∠DBE)",
      "In △ABE and △CBD:",
      "  ∠3=∠4 (given — ∠A=∠C)",
      "  ∠ADE=∠CBD (proved)",
      "  AB=BC (given)",
      "∴ △ABE ≅ △CBD (AAS)",
      "⟹ CD = AE ∎",
    ],
  },
  {
    no: 34, marks: 4,
    q: "In Fig.17, AB=AC, D is a point in the interior of △ABC such that ∠DBC=∠DCB. Prove that AD bisects ∠BAC.",
    solution: [
      "AB=AC ⟹ ∠ABC=∠ACB ... (i)  (base angles of isosceles △)",
      "∠DBC=∠DCB (given) ⟹ DB=DC ... (ii)  (sides opp. equal angles)",
      "∠ABD = ∠ABC−∠DBC = ∠ACB−∠DCB = ∠ACD",
      "In △ABD and △ACD:",
      "  AB=AC, DB=DC, AD=AD (common)",
      "∴ △ABD ≅ △ACD (SSS)",
      "⟹ ∠BAD=∠CAD ⟹ AD bisects ∠BAC ∎",
    ],
  },
];

/* ── Helper components ───────────────────────────────────── */
function Pill({ children, color }) {
  return (
    <span style={{fontSize:10,fontWeight:700,letterSpacing:.7,textTransform:"uppercase",
      color,border:`1px solid ${color}`,borderRadius:4,padding:"2px 7px",marginLeft:8}}>
      {children}
    </span>
  );
}

function McqCard({ item, idx }) {
  const [chosen, setChosen] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const done = chosen !== null;

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
      padding:"18px 20px",marginBottom:14}}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:14}}>
        <span style={{flexShrink:0,width:28,height:28,borderRadius:7,background:"rgba(56,189,248,0.1)",
          color:T.accent,fontFamily:"monospace",fontSize:12,fontWeight:700,
          display:"flex",alignItems:"center",justifyContent:"center"}}>Q{idx+1}</span>
        <p style={{fontSize:14.5,color:T.textPri,margin:0,lineHeight:1.6}}>{item.q}</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:done?12:0}}>
        {item.opts.map((opt,i)=>{
          let bg="rgba(255,255,255,0.03)", border=T.border, color=T.textSec;
          if(done){
            if(i===item.ans){bg="rgba(52,211,153,0.1)";border=T.green;color=T.green;}
            else if(i===chosen){bg="rgba(248,113,113,0.1)";border=T.red;color=T.red;}
            else{color=T.textMute;}
          }
          return (
            <button key={i} disabled={done} onClick={()=>setChosen(i)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                background:bg,border:`1px solid ${border}`,borderRadius:8,
                cursor:done?"default":"pointer",textAlign:"left",color}}>
              <span style={{flexShrink:0,width:22,height:22,borderRadius:5,
                background:"rgba(255,255,255,0.06)",fontFamily:"monospace",fontSize:11,
                fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",
                color:done&&i===item.ans?T.green:done&&i===chosen?T.red:T.textMute}}>
                {String.fromCharCode(65+i)}
              </span>
              <span style={{fontSize:13.5,lineHeight:1.4}}>{opt}</span>
            </button>
          );
        })}
      </div>

      {done && (
        <div>
          <div style={{fontSize:13,color:chosen===item.ans?T.green:T.red,
            fontWeight:600,marginBottom:8}}>
            {chosen===item.ans?"✓ Correct!" : `✗ Incorrect — Answer is (${String.fromCharCode(65+item.ans)})`}
          </div>
          <button onClick={()=>setShowHint(!showHint)}
            style={{background:"none",border:"none",cursor:"pointer",
              color:T.accent,fontSize:12,padding:0,textDecoration:"underline"}}>
            {showHint?"Hide explanation":"Show explanation"}
          </button>
          {showHint && (
            <div style={{marginTop:8,background:"rgba(56,189,248,0.06)",border:`1px solid rgba(56,189,248,0.2)`,
              borderRadius:8,padding:"10px 13px",fontSize:13,color:T.textSec,lineHeight:1.65}}>
              {item.hint}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubjQuestion({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
      padding:"16px 20px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:open?14:0}}>
        <div style={{flexShrink:0}}>
          <div style={{width:32,height:32,borderRadius:8,background:"rgba(167,139,250,0.12)",
            color:T.purple,fontFamily:"monospace",fontSize:12,fontWeight:700,
            display:"flex",alignItems:"center",justifyContent:"center"}}>{item.no}</div>
        </div>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:6,marginBottom:8}}>
            <Pill color={T.orange}>{item.marks} mark{item.marks>1?"s":""}</Pill>
          </div>
          <p style={{fontSize:14.5,color:T.textPri,margin:0,lineHeight:1.7,whiteSpace:"pre-line"}}>{item.q}</p>
        </div>
        <button onClick={()=>setOpen(!open)}
          style={{flexShrink:0,background:"rgba(167,139,250,0.1)",border:`1px solid ${T.purple}`,
            color:T.purple,borderRadius:7,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
          {open?"Hide":"Solution"}
        </button>
      </div>
      {open && (
        <div style={{marginLeft:46,background:"rgba(52,211,153,0.05)",border:`1px solid rgba(52,211,153,0.15)`,
          borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:11,letterSpacing:.7,textTransform:"uppercase",color:T.green,
            fontWeight:700,marginBottom:8}}>Solution</div>
          <ol style={{margin:0,padding:"0 0 0 18px",lineHeight:1.8}}>
            {item.solution.map((s,i)=>(
              <li key={i} style={{fontSize:13.5,color:T.textSec,marginBottom:4}}>{s}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────── */
const TABS = [
  {id:"info",   label:"Paper Info"},
  {id:"secA",   label:"Section A"},
  {id:"secB",   label:"Section B"},
  {id:"secC",   label:"Section C"},
  {id:"secD",   label:"Section D"},
];

export default function Class9MathsSQP1() {
  const [tab, setTab] = useState("info");
  const mainRef = useRef(null);

  const switchTab = (id) => {
    setTab(id);
    if(mainRef.current) mainRef.current.scrollTop = 0;
  };

  const secInfo = {
    secA:{qs:"1–8", each:1, total:8, label:"MCQ", color:T.accent},
    secB:{qs:"9–14", each:2, total:12, label:"Short Answer", color:T.green},
    secC:{qs:"15–24", each:3, total:30, label:"3-Mark Problems", color:T.yellow},
    secD:{qs:"25–34", each:4, total:40, label:"Long Answer", color:T.orange},
  };

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,fontFamily:"'Inter',sans-serif"}}>

      {/* ── Sidebar ── */}
      <div style={{width:200,flexShrink:0,background:T.surface,borderRight:`1px solid ${T.border}`,
        display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:T.accent,fontWeight:700,marginBottom:4}}>
            Class 9 · Maths
          </div>
          <div style={{fontSize:13,fontWeight:800,color:T.textPri,lineHeight:1.3}}>SQP — SA·I</div>
          <div style={{fontSize:11,color:T.textMute,marginTop:3}}>90 Marks · 3 Hours</div>
        </div>
        <nav style={{padding:"8px 6px",flex:1}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>switchTab(t.id)}
              style={{display:"flex",alignItems:"center",gap:9,width:"100%",textAlign:"left",
                padding:"9px 10px",borderRadius:8,border:"none",cursor:"pointer",marginBottom:3,
                background:tab===t.id?"rgba(56,189,248,0.1)":"transparent",
                borderLeft:tab===t.id?`2px solid ${T.accent}`:"2px solid transparent",
                color:tab===t.id?T.accent:T.textSec,
                fontSize:13.5,fontWeight:tab===t.id?700:400,transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </nav>
        <div style={{padding:"10px 14px",borderTop:`1px solid ${T.border}`,
          fontSize:11,color:T.textMute,lineHeight:1.65}}>
          Section A: interactive MCQ<br/>
          B–D: tap Solution to reveal
        </div>
      </div>

      {/* ── Main ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header strip */}
        <div style={{flexShrink:0,padding:"14px 36px",background:T.surface,
          borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:16}}>
          <div>
            <span style={{fontSize:11,letterSpacing:.8,textTransform:"uppercase",
              color:T.purple,fontWeight:700}}>Sample Question Paper · SA-I</span>
            <span style={{fontSize:13,color:T.textMute,marginLeft:16}}>
              {tab==="info"?"Overview" : TABS.find(t=>t.id===tab)?.label}
            </span>
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:16}}>
            {["secA","secB","secC","secD"].map(k=>{
              const s=secInfo[k];
              return (
                <button key={k} onClick={()=>switchTab(k)}
                  style={{background:"none",border:"none",cursor:"pointer",textAlign:"center"}}>
                  <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,color:s.color}}>{s.total}</div>
                  <div style={{fontSize:10,color:T.textMute}}>{k.replace("sec","Sec ")}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"28px 36px"}}>

          {/* ── INFO ── */}
          {tab==="info" && (
            <div>
              <div style={{textAlign:"center",marginBottom:28}}>
                <div style={{fontSize:11,letterSpacing:1.5,textTransform:"uppercase",
                  color:T.purple,fontWeight:700,marginBottom:8}}>Class IX · Mathematics</div>
                <h1 style={{fontSize:26,fontWeight:900,color:T.textPri,margin:"0 0 6px"}}>
                  Sample Question Paper · SA-I
                </h1>
                <div style={{fontSize:14,color:T.textMute}}>Time: 3 hours &nbsp;|&nbsp; Maximum Marks: 90</div>
              </div>

              {/* Blue print */}
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
                overflow:"hidden",marginBottom:24}}>
                <div style={{padding:"12px 18px",background:"rgba(167,139,250,0.08)",
                  borderBottom:`1px solid ${T.border}`}}>
                  <span style={{fontSize:12,fontWeight:700,letterSpacing:.5,textTransform:"uppercase",
                    color:T.purple}}>Blue Print</span>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13.5}}>
                    <thead>
                      <tr style={{background:T.surface}}>
                        {["Unit/Topic","SA-I (1M)","SA-I (2M)","SA-I (3M)","SA-I (4M)","Total"].map(h=>(
                          <th key={h} style={{padding:"10px 14px",textAlign:"left",
                            color:T.textMute,fontSize:11,letterSpacing:.7,textTransform:"uppercase",
                            borderBottom:`1px solid ${T.border}`,fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Number System",  "1(1)","2(1)","6(2)*","8(2)*","17(6)"],
                        ["Algebra/Polynomials","3(3)","4(2)","6(2)","12(3)","25(10)"],
                        ["Geometry",       "2(2)","4(2)*","15(5)*","16(4)","37(13)"],
                        ["Coord. Geometry","–","2(1)","–","4(1)","6(2)"],
                        ["Mensuration",    "2(2)","–","3(1)","–","5(3)"],
                        ["Total",          "8(8)","12(6)","30(10)","40(10)","90(34)"],
                      ].map((row,i)=>(
                        <tr key={i} style={{borderBottom:`1px solid ${T.border}`,
                          background:i===5?"rgba(255,255,255,0.03)":"transparent"}}>
                          {row.map((cell,j)=>(
                            <td key={j} style={{padding:"10px 14px",
                              color:i===5?T.textPri:j===0?T.textPri:T.textSec,
                              fontWeight:i===5||j===0?600:400}}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section cards */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
                {[
                  {sec:"A",qs:"1–8",  n:8,  each:1, total:8,  label:"MCQ",             color:T.accent, desc:"4 choices, select one"},
                  {sec:"B",qs:"9–14", n:6,  each:2, total:12, label:"Short Answer",     color:T.green,  desc:"Show all working"},
                  {sec:"C",qs:"15–24",n:10, each:3, total:30, label:"3-Mark Problems",  color:T.yellow, desc:"Some have OR option"},
                  {sec:"D",qs:"25–34",n:10, each:4, total:40, label:"Long Answer",      color:T.orange, desc:"Some have OR option"},
                ].map(s=>(
                  <button key={s.sec} onClick={()=>switchTab("sec"+s.sec)}
                    style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
                      padding:"16px",textAlign:"left",cursor:"pointer"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <span style={{fontSize:11,fontWeight:700,letterSpacing:.7,textTransform:"uppercase",
                        color:s.color}}>Section {s.sec}</span>
                      <span style={{fontFamily:"monospace",fontSize:20,fontWeight:800,color:s.color}}>{s.total}</span>
                    </div>
                    <div style={{fontSize:13.5,fontWeight:600,color:T.textPri,marginBottom:4}}>{s.label}</div>
                    <div style={{fontSize:12,color:T.textMute}}>Q {s.qs} &nbsp;·&nbsp; {s.n} questions × {s.each}M</div>
                    <div style={{fontSize:11.5,color:T.textMute,marginTop:4}}>{s.desc}</div>
                  </button>
                ))}
              </div>

              <div style={{marginTop:20,background:T.card,border:`1px solid ${T.border}`,
                borderLeft:`3px solid ${T.accent}`,borderRadius:10,padding:"14px 18px"}}>
                <div style={{fontSize:12,letterSpacing:.7,textTransform:"uppercase",color:T.accent,
                  fontWeight:700,marginBottom:8}}>General Instructions</div>
                <ol style={{margin:0,padding:"0 0 0 18px",color:T.textSec,fontSize:13.5,lineHeight:1.8}}>
                  <li>All questions are compulsory.</li>
                  <li>Section A: Q 1–8 carry 1 mark each. Four options are given; select only one.</li>
                  <li>Section B: Q 9–14 carry 2 marks each.</li>
                  <li>Section C: Q 15–24 carry 3 marks each.</li>
                  <li>Section D: Q 25–34 carry 4 marks each.</li>
                  <li>Use of calculator is not permitted.</li>
                </ol>
              </div>
            </div>
          )}

          {/* ── SECTION A ── */}
          {tab==="secA" && (
            <div>
              <div style={{marginBottom:20}}>
                <h2 style={{fontSize:20,fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>Section A</h2>
                <p style={{fontSize:13.5,color:T.textSec,margin:0}}>
                  Q 1–8 carry 1 mark each. Click an option to check your answer.
                </p>
              </div>
              {MCQ.map((item,i)=><McqCard key={i} item={item} idx={i}/>)}
            </div>
          )}

          {/* ── SECTION B ── */}
          {tab==="secB" && (
            <div>
              <div style={{marginBottom:20}}>
                <h2 style={{fontSize:20,fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>Section B</h2>
                <p style={{fontSize:13.5,color:T.textSec,margin:0}}>Q 9–14 carry 2 marks each.</p>
              </div>
              {SECB.map(item=><SubjQuestion key={item.no} item={item}/>)}
            </div>
          )}

          {/* ── SECTION C ── */}
          {tab==="secC" && (
            <div>
              <div style={{marginBottom:20}}>
                <h2 style={{fontSize:20,fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>Section C</h2>
                <p style={{fontSize:13.5,color:T.textSec,margin:0}}>Q 15–24 carry 3 marks each. Some questions have an OR alternative.</p>
              </div>
              {SECC.map(item=><SubjQuestion key={item.no} item={item}/>)}
            </div>
          )}

          {/* ── SECTION D ── */}
          {tab==="secD" && (
            <div>
              <div style={{marginBottom:20}}>
                <h2 style={{fontSize:20,fontWeight:800,color:T.textPri,margin:"0 0 6px"}}>Section D</h2>
                <p style={{fontSize:13.5,color:T.textSec,margin:0}}>Q 25–34 carry 4 marks each. Some questions have an OR alternative.</p>
              </div>
              {SECD.map(item=><SubjQuestion key={item.no} item={item}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
